/* =============================================================================
   ics-UI FACADE - the auto-mount bootstrap. Registry-driven and safe on
   AJAX-injected fragments: a component is found by ITS OWN registered selector,
   never by a broad page-wide scan that can over-match. Entry points:
     - mount(root): scan Registry.mountable() selectors under root and
       construct. Idempotent by construction - re-scanning a page never
       double-binds, so a fragment injector just calls Ics.mount(fragment).
     - unmount(root): destroy every auto-mountable instance under root before
       removing a fragment, so a long-lived session never accumulates
       handlers or timers.
     - installAutoMount(): the ORDERING CONTRACT - the bundle loads in the page
       shell's HEAD, so this registers BEFORE any per-view ready block and
       mounting completes before per-view glue calls
       $('#x').icsDatebox('getValue').
     - installMutationMount(): mount declared markup that arrives after ready
       by any injection path.
     - parse(ctx) / Ics.parse: the OWN subtree re-parse - every loaded
       bundle's mount over one root, synchronously (see below).
   ============================================================================= */
import { Component } from '../core/component.js';
import { Registry } from '../services/registry.js';

/** elements under root (including root itself) matching a selector */
function scan(root, selector) {
  const found = Array.from(root.querySelectorAll(selector));
  if (root !== globalThis.document && root.matches && root.matches(selector)) found.unshift(root);
  return found;
}

/** one line per failed element; identity first so the console points straight at the markup */
function _mountFailed(name, el, err) {
  const id = el && el.id ? '#' + el.id : (el && el.tagName ? '<' + el.tagName.toLowerCase() + '>' : '(element)');
  console.error('[ics] mount failed: ' + name + ' on ' + id, err);
}

/**
 * Mount every registered auto-mount component under root (default: whole
 * document). Safe to call repeatedly and on injected fragments.
 * EVERY element mounts in ISOLATION: one component throwing does not stop the
 * others from mounting (a single throw must never abort the pass and leave the
 * rest of the page unmounted). Each failure logs to the console with the
 * component + element identity; a pass with failures raises ONE summarizing
 * error toast (never one per element), through the same guarded bridge the
 * components use - a page without the messager logs only.
 * @param {Document|HTMLElement} [root]
 * @returns {number} instances alive under root after the scan
 */
export function mount(root = globalThis.document) {
  let n = 0, failed = 0;
  for (const { Ctor, selector } of Registry.mountable()) {
    for (const el of scan(root, selector)) {
      try { new Ctor(el); n++; }
      catch (err) { failed++; _mountFailed(selector, el, err); }   // selector, not Ctor.name: survives minification
    }
  }
  if (failed) _reportMountFailures(failed);
  return n;
}

/* ONE toast per burst, not one per pass. mount() is called PER ADDED ELEMENT by the
   MutationObserver below, so "one summarizing toast per pass" would still be a
   toast storm when a class of injected markup throws (a malformed data-options on
   rows a grid renderer injects). Failures are accumulated and flushed once on the
   next frame, so a whole burst yields a single card. */
let _pendingFailures = 0, _flushQueued = false;
function _reportMountFailures(count) {
  _pendingFailures += count;
  if (_flushQueued) return;
  _flushQueued = true;
  const flush = () => {
    _flushQueued = false;
    const n = _pendingFailures; _pendingFailures = 0;
    if (!n) return;
    const g = globalThis;
    if (typeof g.IcsToastErr === 'function') {
      g.IcsToastErr(n + ' component' + (n === 1 ? '' : 's') + ' failed to mount - see the browser console.');
    }
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(flush);
  else setTimeout(flush, 0);
}

/**
 * Destroy every auto-mountable instance under root (call BEFORE removing an
 * injected fragment). Programmatically-created components inside root that
 * also match a registered selector are covered; purely imperative ones own
 * their own destroy() (a modal built outside the Registry, for one - its opener disposes it).
 * @param {Document|HTMLElement} [root]
 * @returns {number} instances destroyed
 */
export function unmount(root = globalThis.document) {
  let n = 0;
  for (const { selector } of Registry.mountable()) {
    for (const el of scan(root, selector)) {
      const inst = Component.get(el);
      if (!inst) continue;
      // same isolation as mount(): one broken destroy() must not leave the rest
      // of the fragment's instances alive with their handlers and timers
      try { inst.destroy(); n++; }
      catch (err) { _mountFailed(selector + ' destroy', el, err); }
    }
  }
  return n;
}

/* =============================================================================
   THE OWN RE-PARSE - Ics.parse(ctx). The estate idiom "inject markup, then
   re-parse the subtree" used to go through the vendor page parser; the own
   equivalent mounts EVERY loaded bundle's registered components over that
   subtree, SYNCHRONOUSLY. The MutationObserver below also sees the injection,
   but only on a later microtask - a caller that touches the mounted result on
   the very next line needs the synchronous pass. Registry is PER BUNDLE, so
   each bundle's copy of this module registers its own mount() in ONE
   realm-shared list keyed by Symbol.for (the window-stack precedent), and
   parse(ctx) runs the whole list. Published CLAIM-IF-ABSENT as Ics.parse by
   whichever bundle gets there first; the claimed copy serves every bundle
   because the list is realm-shared.
   ============================================================================= */
const _PARSE_MOUNTS = Symbol.for('ics.parse.mounts');

function _parseMounts() {
  const g = globalThis;
  if (!g[_PARSE_MOUNTS]) g[_PARSE_MOUNTS] = [];
  return g[_PARSE_MOUNTS];
}

/** normalize a re-parse ctx (jQuery set | selector | element | document | nothing) to a root */
function _ctxToRoot(ctx) {
  if (ctx == null) return globalThis.document || null;
  if (ctx.jquery) return ctx[0] || null;
  if (typeof ctx === 'string') return globalThis.document ? globalThis.document.querySelector(ctx) : null;
  if (ctx.nodeType === 1 || ctx.nodeType === 9) return ctx;
  return null;
}

/**
 * Mount every loaded bundle's auto-mount components under ctx, synchronously.
 * @param {*} [ctx] jQuery set | selector | element | document; default = document
 * @returns {number} instances alive under the root after the sweep
 */
export function parse(ctx) {
  const root = _ctxToRoot(ctx);
  if (!root) return 0;
  let n = 0;
  for (const m of _parseMounts()) {
    // same isolation doctrine as mount(): one bundle failing must not leave
    // the remaining bundles' families unmounted
    try { n += m(root); }
    catch (err) { console.error('[ics] parse: a bundle\'s mount pass failed', err); }
  }
  return n;
}

/* claim-if-absent, ALWAYS: the first component bundle publishes `Ics` only at
   the END of its entry body, so its own copy claims at ready; every later
   bundle finds `Ics` already live and claims immediately at load. */
function _publishParse() {
  const I = globalThis.Ics;
  if (I && !I.parse) I.parse = parse;
}

let _installed = false;
let _observer = null;

/**
 * SAFETY NET for pages whose views never call Ics.mount(): mount declared
 * `[data-ics=…]` markup that arrives AFTER ready by ANY injection path - grid
 * cell renderers, `.html()`/`.append()` fragments, window content built from
 * strings. A MutationObserver over document.body; only ELEMENT additions are
 * inspected, only registered selectors are queried (the constructor is
 * idempotent, so a node mounted twice is a no-op). EVERY bundle entry installs
 * it: a view is not required to call Ics.mount(), so without it markup injected
 * after ready would stay raw. The observer's cost is negligible; each extra
 * bundle on a page adds one more observer pass per injection, which is why a
 * component that extends another ships inside that component's bundle.
 * @returns {boolean} true if installed by this call
 */
export function installMutationMount() {
  if (_observer || typeof document === 'undefined' || typeof MutationObserver === 'undefined') return false;
  const start = () => {
    if (_observer || !document.body) return;
    _observer = new MutationObserver((records) => {
      for (const r of records) {
        for (const node of r.addedNodes) {
          if (node.nodeType !== 1) continue;
          mount(/** @type {HTMLElement} */ (node));
        }
      }
    });
    _observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);
  return true;
}

/**
 * Install the once-per-page auto-mount (idempotent). ORDERING: when jQuery is
 * present we register via $(fn) - the SAME ready queue per-view glue uses - so
 * a head-loaded bundle lands AHEAD of every body-registered $(document).ready
 * block. A raw DOMContentLoaded listener would fire AFTER jQuery's queue and
 * break that contract. Fallback (no jQuery): DOMContentLoaded, or an immediate
 * mount when the document is already past it.
 */
export function installAutoMount() {
  if (_installed || typeof document === 'undefined') return;
  _installed = true;
  _parseMounts().push(mount);   // this bundle's families join the Ics.parse sweep
  _publishParse();
  const $ = globalThis.jQuery || globalThis.$;
  if ($ && typeof $ === 'function' && $.fn) { $(() => { _publishParse(); mount(); }); return; }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { _publishParse(); mount(); });
  } else {
    _publishParse(); mount();
  }
}
